import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar, 
  Avatar, 
  TextField, 
  Button, 
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  FormControlLabel,
  Divider,
  Grid,
  Tooltip,
  Tabs,
  Tab,
  Stack,
  LinearProgress,
  Slider
} from '@mui/material';
import { 
  Send as SendIcon, 
  Receipt as ReceiptIcon,
  CheckCircle as AcceptIcon,
  Person as PersonIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Queue as QueueIcon,
  AttachFile as AttachFileIcon,
  Mic as MicIcon,
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  Schedule as ScheduleIcon,
  ErrorOutline as ErrorOutlineIcon,
  Replay as ReplayIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  SwapHoriz as TransferIcon,
  Visibility as SupervisionIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { styled, useTheme } from '@mui/material/styles';
import ticketService from '../../services/ticketService';
import queueService from '../../services/queueService';
import contactsService from '../../services/contactsService';
import operationsService from '../../services/operationsService';
import usersService from '../../services/usersService';
import useAuth from '../../hooks/useAuth';
import unifiedSocket from '../../services/unifiedSocket';

// Styled components para um visual moderno
const StyledPaper = styled(Paper)(({ theme }) => ({
  background: theme.palette.background.paper,
  color: theme.palette.text.primary,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`
}));

const IntegratedWhatsAppChat = () => {
  const theme = useTheme();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [socketConnected, setSocketConnected] = useState(false);
  const [messagesByTicket, setMessagesByTicket] = useState({});
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [protocolDialog, setProtocolDialog] = useState(false);
  const [generatedProtocol, setGeneratedProtocol] = useState('');
  const [queues, setQueues] = useState([]);
  const [selectedQueue, setSelectedQueue] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [messageSearch, setMessageSearch] = useState('');
  const [messageTypeFilter, setMessageTypeFilter] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [workspaceTab, setWorkspaceTab] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const isSendingRef = useRef(false);
  const [workspace, setWorkspace] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [notes, setNotes] = useState([]);
  const [contactProfile, setContactProfile] = useState(null);
  const [contactTimeline, setContactTimeline] = useState([]);
  const [contactTickets, setContactTickets] = useState([]);
  const [contactNotesDraft, setContactNotesDraft] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [loadingWorkspace, setLoadingWorkspace] = useState(false);
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const selectedConversationRef = useRef(null);
  const [pendingMedia, setPendingMedia] = useState(null);
  const [pendingAudio, setPendingAudio] = useState(null);
  const [pendingMediaPreviewUrl, setPendingMediaPreviewUrl] = useState('');
  const [pendingAudioPreviewUrl, setPendingAudioPreviewUrl] = useState('');
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const recordingIntervalRef = useRef(null);
  const activeAudioRef = useRef(null);
  const [activeAudioMessageId, setActiveAudioMessageId] = useState(null);
  const [confirmAssignOpen, setConfirmAssignOpen] = useState(false);
  const [assignTargetId, setAssignTargetId] = useState(null);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferToUserId, setTransferToUserId] = useState('');
  const [operators, setOperators] = useState([]);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusDialogValue, setStatusDialogValue] = useState('closed');
  const [statusDialogNote, setStatusDialogNote] = useState('');
  const [supervisionDialogOpen, setSupervisionDialogOpen] = useState(false);
  const [watchers, setWatchers] = useState([]);
  const [supervisionCandidates, setSupervisionCandidates] = useState([]);
  const [supervisionUserId, setSupervisionUserId] = useState('');
  const [messageInputKey, setMessageInputKey] = useState(0);
  const { user: authUser } = useAuth();
  const currentUser = authUser?.user || authUser || null;
  const token = authUser?.token || null;
  const socketUserId = currentUser?.id || null;
  const socketCompanyId = currentUser?.companyId || 1;

  const isMountedRef = useRef(false);
  const timeoutsRef = useRef([]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      const timeouts = timeoutsRef.current || [];
      timeouts.forEach((id) => {
        try { clearTimeout(id); } catch {}
      });
      timeoutsRef.current = [];
    };
  }, []);

  const scheduleTimeout = useCallback((fn, ms) => {
    const id = setTimeout(() => {
      if (!isMountedRef.current) return;
      fn();
    }, ms);
    timeoutsRef.current.push(id);
    return id;
  }, []);

  const queuesRef = useRef([]);
  useEffect(() => {
    queuesRef.current = queues;
  }, [queues]);

  const applyQueueMapping = useCallback((ticketOrMessage) => {
    try {
      const queueId = ticketOrMessage?.queueId || ticketOrMessage?.queue?.id;
      if (queueId) {
        const q = queuesRef.current.find(q => String(q.id) === String(queueId));
        return q ? { id: q.id, name: q.name, color: q.color } : null;
      }
      const number = ticketOrMessage?.contact?.number || ticketOrMessage?.number;
      if (!number) return null;
      const qGuess = queuesRef.current.find(q => q.name && number && String(q.name).includes(String(number).slice(-4)));
      return qGuess ? { id: qGuess.id, name: qGuess.name, color: qGuess.color } : null;
    } catch { return null; }
  }, []);

  // Manter referência atual da conversa selecionada
  useEffect(() => {
    selectedConversationRef.current = selectedConversation || null;
  }, [selectedConversation]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!selectedConversation?.id) return;
      if (!isMountedRef.current || cancelled) return;
      setLoadingWorkspace(true);
      try {
        const [w, t, n] = await Promise.all([
          ticketService.getWorkspace(selectedConversation.id),
          ticketService.getTimeline(selectedConversation.id),
          ticketService.getNotes(selectedConversation.id)
        ]);
        if (!isMountedRef.current || cancelled) return;
        const ticket = w?.ticket || w?.data?.ticket || w?.data || null;
        setWorkspace(ticket);
        setTimeline(t?.timeline || t?.data?.timeline || []);
        setNotes(n?.notes || n?.data?.notes || []);
        const contactId = ticket?.contact?.id || ticket?.contactId || null;
        if (contactId) {
          const [cp, ct, ctk] = await Promise.all([
            contactsService.getContactById(contactId),
            contactsService.getContactTimeline(contactId),
            contactsService.getContactTickets(contactId, { limit: 10, page: 1 })
          ]);
          if (!isMountedRef.current || cancelled) return;
          const contact = cp?.contact || cp?.data?.contact || cp?.data || null;
          setContactProfile(contact);
          setContactNotesDraft(contact?.notes || '');
          setContactTimeline(ct?.timeline || ct?.data?.timeline || []);
          setContactTickets(ctk?.tickets || ctk?.data?.tickets || []);
        } else {
          setContactProfile(null);
          setContactNotesDraft('');
          setContactTimeline([]);
          setContactTickets([]);
        }
      } catch {
        if (!isMountedRef.current || cancelled) return;
        setWorkspace(null);
        setTimeline([]);
        setNotes([]);
        setContactProfile(null);
        setContactNotesDraft('');
        setContactTimeline([]);
        setContactTickets([]);
      } finally {
        if (!isMountedRef.current || cancelled) return;
        setLoadingWorkspace(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [selectedConversation?.id]);

  // Conectar WebSocket uma vez
  useEffect(() => {
    if (!token) return;
    if (!socketUserId) return;
    const companyId = socketCompanyId;
    unifiedSocket.connect({ id: socketUserId, companyId, token });
    const socket = unifiedSocket.getSocket?.();
    if (!socket) return;
    socketRef.current = socket;

    const onMessageEvent = (data) => {
      const action = data?.action;
      const message = data?.message || data;
      const ticket = data?.ticket || message?.ticket;
      const msgTicketId = message?.ticketId || data?.ticketId || ticket?.id;
      const normalizedMessage = {
        ...message,
        timestamp: message?.timestamp || message?.createdAt || Date.now()
      };

      setConversations(prev => {
        const queueInfo = applyQueueMapping(ticket || message);
        return prev.map(conv => {
          if (msgTicketId && conv.id === msgTicketId) {
            const isCurrent = selectedConversationRef.current && conv.id === selectedConversationRef.current.id;
            return {
              ...conv,
              lastMessage: message?.body || conv.lastMessage,
              queue: conv.queue || queueInfo || conv.queue,
              unread: isCurrent ? 0 : (Number(conv.unread) || 0) + (message?.fromMe ? 0 : 1)
            };
          }
          return conv;
        });
      });

      if (selectedConversationRef.current && msgTicketId === selectedConversationRef.current.id) {
        if (action === 'update') {
          setMessages(prev => prev.map(m => (
            ((m.id !== undefined && m.id !== null && normalizedMessage.id !== undefined && normalizedMessage.id !== null && String(m.id) === String(normalizedMessage.id)) ||
              (m.messageId && normalizedMessage.messageId && String(m.messageId) === String(normalizedMessage.messageId)))
              ? { ...m, ...normalizedMessage }
              : m
          )));
        } else {
          setMessages(prev => {
            const exists = prev.some(m => (
              (m.id !== undefined && m.id !== null && normalizedMessage.id !== undefined && normalizedMessage.id !== null && String(m.id) === String(normalizedMessage.id)) ||
              (m.messageId && normalizedMessage.messageId && String(m.messageId) === String(normalizedMessage.messageId))
            ));
            if (exists) {
              return prev.map(m => (
                (m.id !== undefined && m.id !== null && normalizedMessage.id !== undefined && normalizedMessage.id !== null && String(m.id) === String(normalizedMessage.id)) ||
                (m.messageId && normalizedMessage.messageId && String(m.messageId) === String(normalizedMessage.messageId))
                  ? { ...m, ...normalizedMessage }
                  : m
              ));
            }
            return [...prev, normalizedMessage];
          });
          scheduleTimeout(() => scrollToBottom(), 50);
        }
      }
    };

    const onTicketEvent = (data) => {
      const updatedTicket = data?.ticket || data;
      setConversations(prev => prev.map(conv => (
        updatedTicket && conv.id === updatedTicket.id ? { ...conv, ...updatedTicket } : conv
      )));
      setSelectedConversation(prev => (
        prev && updatedTicket && prev.id === updatedTicket.id ? { ...prev, ...updatedTicket } : prev
      ));
    };

    const onConnect = () => setSocketConnected(true);
    const onDisconnect = () => setSocketConnected(false);
    const onConnectError = () => setSocketConnected(false);
    const onCreated = (payload) => onMessageEvent({ action: 'create', message: payload?.message, ticket: payload?.ticket, ticketId: payload?.ticketId });
    const onUpdated = (payload) => onTicketEvent({ action: 'update', ticket: payload?.ticket });

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    socket.on(`company-${companyId}-appMessage`, onMessageEvent);
    socket.on(`company-${companyId}-ticket`, onTicketEvent);
    socket.on('message.created', onCreated);
    socket.on('ticket.updated', onUpdated);

    return () => {
      try { socket.off('connect', onConnect); } catch {}
      try { socket.off('disconnect', onDisconnect); } catch {}
      try { socket.off('connect_error', onConnectError); } catch {}
      try { socket.off(`company-${companyId}-appMessage`, onMessageEvent); } catch {}
      try { socket.off(`company-${companyId}-ticket`, onTicketEvent); } catch {}
      try { socket.off('message.created', onCreated); } catch {}
      try { socket.off('ticket.updated', onUpdated); } catch {}
    };
  }, [socketUserId, socketCompanyId, token, applyQueueMapping, scheduleTimeout]);

  // Buscar filas disponíveis
  const fetchQueues = useCallback(async () => {
    if (!isMountedRef.current) return;
    setLoading(true);
    try {
      const response = await queueService.getQueues();
      const queuesData = response?.data || response || [];
      if (!isMountedRef.current) return;
      setQueues(Array.isArray(queuesData) ? queuesData : []);
    } catch (_) {
      if (!isMountedRef.current) return;
      setQueues([]);
    } finally {
      if (!isMountedRef.current) return;
      setLoading(false);
    }
  }, []);

  const dedupeMessages = useCallback((list) => {
    const arr = Array.isArray(list) ? list : [];
    const seen = new Map();
    for (const m of arr) {
      const key =
        (m?.id !== undefined && m?.id !== null) ? `id:${m.id}` :
          (m?.messageId ? `mid:${m.messageId}` : null);
      if (!key) continue;
      const prev = seen.get(key);
      seen.set(key, prev ? { ...prev, ...m } : m);
    }
    const withKey = [];
    for (const m of arr) {
      const key =
        (m?.id !== undefined && m?.id !== null) ? `id:${m.id}` :
          (m?.messageId ? `mid:${m.messageId}` : null);
      if (!key) {
        withKey.push(m);
        continue;
      }
      const item = seen.get(key);
      if (item) {
        withKey.push(item);
        seen.delete(key);
      }
    }
    return withKey;
  }, []);

  const upsertMessage = useCallback((prev, incoming) => {
    const list = Array.isArray(prev) ? prev : [];
    if (!incoming) return list;
    const idx = list.findIndex(m => (
      (m?.id !== undefined && m?.id !== null && incoming?.id !== undefined && incoming?.id !== null && String(m.id) === String(incoming.id)) ||
      (m?.messageId && incoming?.messageId && String(m.messageId) === String(incoming.messageId))
    ));
    if (idx === -1) return [...list, incoming];
    const next = list.slice();
    next[idx] = { ...next[idx], ...incoming };
    return next;
  }, []);

  const renderedMessages = useMemo(() => dedupeMessages(messages), [dedupeMessages, messages]);

  // Buscar conversas/tickets
  const fetchConversations = useCallback(async () => {
    if (!isMountedRef.current) return;
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (selectedQueue && selectedQueue !== 'all') params.queueId = selectedQueue;
      const response = await ticketService.getTickets(params);
      const ticketsData = Array.isArray(response?.tickets) ? response.tickets : [];
      if (!isMountedRef.current) return;
      setConversations(ticketsData);
    } catch (_) {
      if (!isMountedRef.current) return;
      setConversations([]);
    } finally {
      if (!isMountedRef.current) return;
      setLoading(false);
    }
  }, [statusFilter, selectedQueue]);

  // Carregar dados iniciais
  useEffect(() => {
    fetchQueues();
    fetchConversations();
  }, [fetchQueues, fetchConversations]);

  const inferMediaType = (file) => {
    const t = String(file?.type || '');
    if (t.startsWith('image/')) {
      if (t === 'image/webp') return 'sticker';
      return 'image';
    }
    if (t.startsWith('audio/')) return 'audio';
    if (t.startsWith('video/')) return 'video';
    return 'document';
  };

  const normalizeUploadFile = (file, fallbackName) => {
    if (!file) return null;
    try {
      if (file instanceof File) return file;
      if (file instanceof Blob) {
        return new File([file], fallbackName, { type: file.type || 'application/octet-stream' });
      }
    } catch (_) {}
    return file;
  };

  const fetchMessages = useCallback(async (ticketId, options = {}) => {
    try {
      const page = options.page || 1;
      const limit = options.limit || 50;
      const search = options.search ?? messageSearch;
      const mediaType = (options.mediaType ?? messageTypeFilter) === 'all' ? undefined : (options.mediaType ?? messageTypeFilter);

      const response = await ticketService.getTicketMessagesPaged(ticketId, {
        page,
        limit,
        search,
        mediaType
      });
      const messagesData = response?.messages || response?.data?.messages || [];

      const rows = dedupeMessages(messagesData);
      if (!isMountedRef.current) return;
      setMessagesByTicket(prevMap => ({ ...prevMap, [ticketId]: rows }));
      setMessages(rows);
  
      scheduleTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      if (!isMountedRef.current) return;
      setMessages(prev => prev.length ? prev : (messagesByTicket[ticketId] || []));
    }
  }, [messageSearch, messageTypeFilter, messagesByTicket, dedupeMessages, scheduleTimeout]);

  useEffect(() => {
    if (!selectedConversation?.id) return;
    if (socketConnected) return;
    const ticketId = selectedConversation.id;
    const intervalId = setInterval(() => {
      fetchMessages(ticketId, { page: 1 });
    }, 5000);
    return () => clearInterval(intervalId);
  }, [selectedConversation?.id, socketConnected, fetchMessages]);

  // Recarregar conversas quando filtros mudarem
  useEffect(() => {
    fetchConversations();
    if (selectedConversation?.id) {
      const cached = messagesByTicket[selectedConversation.id];
      if (Array.isArray(cached)) {
        setMessages(dedupeMessages(cached));
      } else {
        fetchMessages(selectedConversation.id, { page: 1 });
      }
    }
  }, [fetchConversations, selectedConversation?.id, messagesByTicket, fetchMessages, dedupeMessages]);

  const handleConversationSelect = (conversation) => {
    setSelectedConversation(conversation);
    setReplyTo(null);
    setWorkspaceTab(0);
    if (!conversation.userId || String(conversation.userId) !== String(currentUser?.id)) {
      openConfirmAssign(conversation.id);
    }
    const cached = messagesByTicket[conversation.id];
    if (Array.isArray(cached) && cached.length) {
      setMessages(dedupeMessages(cached));
      scheduleTimeout(() => scrollToBottom(), 50);
    } else {
      fetchMessages(conversation.id, { page: 1 });
    }
    ticketService.markRead(conversation.id).catch(() => {});
  };

  const handleSendMessage = async () => {
    if (isSendingRef.current) return;
    const assignedToMe =
      selectedConversation &&
      selectedConversation?.userId !== undefined &&
      selectedConversation?.userId !== null &&
      currentUser?.id !== undefined &&
      currentUser?.id !== null &&
      String(selectedConversation.userId) === String(currentUser.id);
    if (!selectedConversation) return;
    if (!assignedToMe) {
      try {
        await ticketService.acceptTicket(selectedConversation.id);
        if (!isMountedRef.current) return;
        setConversations(prev => prev.map(c => (
          c && c.id === selectedConversation.id ? { ...c, userId: currentUser.id } : c
        )));
        setSelectedConversation(prev => (
          prev && prev.id === selectedConversation.id ? { ...prev, userId: currentUser.id } : prev
        ));
      } catch (err) {
        toast.error(err?.message || 'Não foi possível assumir o atendimento');
        return;
      }
    }
    if (!newMessage.trim() && !pendingMedia && !pendingAudio) return;

    isSendingRef.current = true;
    setIsSending(true);
    try {
      if (pendingMedia) {
        const file = normalizeUploadFile(pendingMedia, 'arquivo');
        const resp = await ticketService.sendMediaMessage(selectedConversation.id, file, { type: inferMediaType(file), caption: newMessage || undefined });
        const created = resp?.message || resp?.data?.message || null;
        setPendingMedia(null);
        setNewMessage('');
        setMessageInputKey(Date.now());
        if (created) {
          const m = { ...created, timestamp: created.timestamp || created.createdAt || Date.now() };
          setMessages(prev => upsertMessage(prev, m));
        }
        scheduleTimeout(() => fetchMessages(selectedConversation.id, { page: 1 }), 800);
        return;
      }
      if (pendingAudio) {
        const file = normalizeUploadFile(pendingAudio, 'audio.webm');
        const mime = String(file?.type || '').toLowerCase();
        const preferPtt = mime.includes('ogg') || mime.includes('opus');
        const resp = await ticketService.sendMediaMessage(selectedConversation.id, file, { type: inferMediaType(file), caption: newMessage || undefined, ptt: preferPtt });
        const created = resp?.message || resp?.data?.message || null;
        setPendingAudio(null);
        setNewMessage('');
        setMessageInputKey(Date.now());
        if (created) {
          const m = { ...created, timestamp: created.timestamp || created.createdAt || Date.now() };
          setMessages(prev => upsertMessage(prev, m));
        }
        scheduleTimeout(() => fetchMessages(selectedConversation.id, { page: 1 }), 800);
        return;
      }
      const messageData = {
        body: newMessage,
        fromMe: true,
        ...(replyTo?.messageId ? { quotedMsgId: replyTo.messageId } : {})
      };

      const resp = await ticketService.sendMessage(selectedConversation.id, messageData);
      const created = resp?.message || resp?.data?.message || null;
      const sendError = resp?.error || resp?.data?.error || null;
      if (sendError) {
        toast.error(String(sendError));
      }
      setNewMessage('');
      setReplyTo(null);
      setMessageInputKey(Date.now());
      
      const tempMessage = {
        id: Date.now(),
        body: messageData.body,
        fromMe: true,
        timestamp: new Date(),
        ticketId: selectedConversation.id,
        temp: true
      };
      if (created) {
        const m = { ...created, timestamp: created.timestamp || created.createdAt || Date.now() };
        setMessages(prev => upsertMessage(prev, m));
      } else {
        setMessages(prev => [...prev, tempMessage]);
      }
  
      // Se desejar, agendar uma tentativa de sincronização leve depois
      scheduleTimeout(() => fetchMessages(selectedConversation.id, { page: 1 }), 800);
    } catch (error) {
      toast.error(error?.message || 'Erro ao enviar mensagem');
      setPendingMedia(null);
      setPendingAudio(null);
    } finally {
      isSendingRef.current = false;
      setIsSending(false);
    }
  };

  const handleFileSelect = (evt) => {
    const file = evt.target.files && evt.target.files[0];
    if (!file) return;
    setPendingMedia(file);
    try { evt.target.value = ''; } catch (_) {}
  };

  // Gravação de áudio via microfone
  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Seu navegador não suporta gravação de áudio.');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const candidates = [
        'audio/ogg;codecs=opus',
        'audio/webm;codecs=opus',
        'audio/webm'
      ];
      const chosenMime = candidates.find(t => {
        try { return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t); } catch (_) { return false; }
      }) || '';
      const recorder = chosenMime ? new MediaRecorder(stream, { mimeType: chosenMime }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const mime = chosenMime || recorder.mimeType || 'audio/webm';
        const isOgg = String(mime).toLowerCase().includes('ogg');
        const ext = isOgg ? 'ogg' : 'webm';
        const blob = new Blob(audioChunksRef.current, { type: mime });
        const file = new File([blob], `gravacao-${Date.now()}.${ext}`, { type: mime });
        setPendingAudio(file);
        audioChunksRef.current = [];
        try { stream.getTracks().forEach(t => t.stop()); } catch {}
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      try {
        if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
      } catch (_) {}
      setRecordingSeconds(0);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingSeconds(s => s + 1);
      }, 1000);
      setIsRecording(true);
    } catch (err) {
      toast.error('Falha ao iniciar gravação de áudio');
    }
  };

  const stopRecording = () => {
    try {
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.stop();
      }
    } catch (err) {
      toast.error('Falha ao parar gravação');
    } finally {
      setIsRecording(false);
      try {
        if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
      } catch (_) {}
      recordingIntervalRef.current = null;
    }
  };

  useEffect(() => {
    if (!pendingMedia) {
      setPendingMediaPreviewUrl('');
      return;
    }
    try {
      const url = URL.createObjectURL(pendingMedia);
      setPendingMediaPreviewUrl(url);
      return () => {
        try { URL.revokeObjectURL(url); } catch (_) {}
      };
    } catch (_) {
      setPendingMediaPreviewUrl('');
    }
  }, [pendingMedia]);

  useEffect(() => {
    if (!pendingAudio) {
      setPendingAudioPreviewUrl('');
      return;
    }
    try {
      const url = URL.createObjectURL(pendingAudio);
      setPendingAudioPreviewUrl(url);
      return () => {
        try { URL.revokeObjectURL(url); } catch (_) {}
      };
    } catch (_) {
      setPendingAudioPreviewUrl('');
    }
  }, [pendingAudio]);

  const handleAcceptTicket = async (ticketId) => {
    try {
      await ticketService.acceptTicket(ticketId);
      // Atualizar estados imediatamente para liberar envio
      setConversations(prev => prev.map(conv => (
        conv.id === ticketId ? { ...conv, userId: currentUser?.id || conv.userId } : conv
      )));
      setSelectedConversation(prev => (
        prev && prev.id === ticketId ? { ...prev, userId: currentUser?.id || prev.userId } : prev
      ));
      // Atualiza lista em background
      fetchConversations();
    } catch (error) {
      // Fallback: atribuir via updateTicket e enviar protocolo manualmente
      try {
        await ticketService.updateTicketStatus(ticketId, 'assigned', currentUser?.id || null);
        setConversations(prev => prev.map(conv => (
          conv.id === ticketId ? { ...conv, userId: currentUser?.id || conv.userId } : conv
        )));
        setSelectedConversation(prev => (
          prev && prev.id === ticketId ? { ...prev, userId: currentUser?.id || prev.userId } : prev
        ));
      } catch (innerErr) {
        const msg = error?.message || innerErr?.message || 'Erro ao assumir ticket';
        toast.error(msg);
      }
    }
  };

  const openConfirmAssign = (ticketId) => {
    setAssignTargetId(ticketId);
    setConfirmAssignOpen(true);
  };

  const closeConfirmAssign = () => {
    setConfirmAssignOpen(false);
    setAssignTargetId(null);
  };

  const confirmAssign = async () => {
    if (!assignTargetId) return;
    await handleAcceptTicket(assignTargetId);
    closeConfirmAssign();
  };

  const openTransferDialog = async () => {
    if (!selectedConversation?.id) return;
    setTransferToUserId('');
    setTransferDialogOpen(true);
    try {
      const resp = await operationsService.getOperatorsSummary();
      const rows = resp?.operators || resp?.data?.operators || [];
      const list = Array.isArray(rows) ? rows : [];
      setOperators(list.filter(o => String(o?.id || o?.userId || '') !== String(currentUser?.id || '')));
    } catch (_) {
      setOperators([]);
    }
  };

  const closeTransferDialog = () => {
    setTransferDialogOpen(false);
    setTransferToUserId('');
  };

  const confirmTransfer = async () => {
    if (!selectedConversation?.id) return;
    const toUserId = Number(transferToUserId);
    if (!toUserId) return;
    try {
      await ticketService.transferTicket(selectedConversation.id, toUserId);
      setConversations(prev => prev.map(c => (
        c && c.id === selectedConversation.id ? { ...c, userId: toUserId, status: 'assigned' } : c
      )));
      setSelectedConversation(prev => (
        prev && prev.id === selectedConversation.id ? { ...prev, userId: toUserId, status: 'assigned' } : prev
      ));
      closeTransferDialog();
      fetchConversations();
      toast.success('Ticket transferido');
    } catch (e) {
      toast.error(e?.message || 'Falha ao transferir ticket');
    }
  };

  const openStatusDialog = () => {
    if (!selectedConversation?.id) return;
    setStatusDialogValue('closed');
    setStatusDialogNote('');
    setStatusDialogOpen(true);
  };

  const closeStatusDialog = () => setStatusDialogOpen(false);

  const confirmStatusChange = async () => {
    if (!selectedConversation?.id) return;
    const note = String(statusDialogNote || '').trim();
    if (!note) {
      toast.error('Observação é obrigatória');
      return;
    }
    try {
      await ticketService.updateTicketStatus(selectedConversation.id, statusDialogValue, null, null, { note });
      setConversations(prev => prev.map(c => (
        c && c.id === selectedConversation.id ? { ...c, status: statusDialogValue } : c
      )));
      setSelectedConversation(prev => (
        prev && prev.id === selectedConversation.id ? { ...prev, status: statusDialogValue } : prev
      ));
      closeStatusDialog();
      fetchConversations();
      toast.success('Status atualizado');
    } catch (e) {
      toast.error(e?.message || 'Falha ao atualizar status');
    }
  };

  const openSupervisionDialog = async () => {
    if (!selectedConversation?.id) return;
    setSupervisionUserId('');
    setSupervisionDialogOpen(true);
    try {
      const [w, u] = await Promise.all([
        ticketService.listWatchers(selectedConversation.id),
        usersService.getUsers()
      ]);
      const watcherRows = w?.watchers || w?.data?.watchers || [];
      setWatchers(Array.isArray(watcherRows) ? watcherRows : []);
      const users = u?.users || u?.data?.users || u || [];
      const list = Array.isArray(users) ? users : [];
      setSupervisionCandidates(list.filter(x => ['admin', 'supervisor', 'manager'].includes(String(x?.type || x?.profile || ''))));
    } catch (_) {
      setWatchers([]);
      setSupervisionCandidates([]);
    }
  };

  const closeSupervisionDialog = () => {
    setSupervisionDialogOpen(false);
    setSupervisionUserId('');
  };

  const addWatcher = async () => {
    if (!selectedConversation?.id) return;
    const uid = Number(supervisionUserId);
    if (!uid) return;
    try {
      await ticketService.addWatcher(selectedConversation.id, uid);
      const w = await ticketService.listWatchers(selectedConversation.id);
      const watcherRows = w?.watchers || w?.data?.watchers || [];
      setWatchers(Array.isArray(watcherRows) ? watcherRows : []);
      setSupervisionUserId('');
      toast.success('Supervisor adicionado');
    } catch (e) {
      toast.error(e?.message || 'Falha ao adicionar supervisor');
    }
  };

  const removeWatcher = async (uid) => {
    if (!selectedConversation?.id) return;
    try {
      await ticketService.removeWatcher(selectedConversation.id, uid);
      setWatchers(prev => prev.filter(w => String(w?.userId || w?.user?.id) !== String(uid)));
      toast.success('Supervisor removido');
    } catch (e) {
      toast.error(e?.message || 'Falha ao remover supervisor');
    }
  };

  const handleGenerateProtocol = async (ticketId) => {
    try {
      const response = await ticketService.generateProtocol(ticketId);
      setGeneratedProtocol(response.protocol);
      setProtocolDialog(true);
    } catch (error) {
      toast.error('Erro ao gerar protocolo');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'info';
      case 'triage': return 'warning';
      case 'queued': return 'warning';
      case 'assigned': return 'primary';
      case 'pending_customer': return 'warning';
      case 'pending_internal': return 'warning';
      case 'resolved': return 'success';
      case 'closed': return 'success';
      case 'reopened': return 'info';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'new': return 'Novo';
      case 'triage': return 'Triagem';
      case 'queued': return 'Na fila';
      case 'assigned': return 'Em atendimento';
      case 'pending_customer': return 'Pendente cliente';
      case 'pending_internal': return 'Pendente interno';
      case 'resolved': return 'Resolvido';
      case 'closed': return 'Fechado';
      case 'reopened': return 'Reaberto';
      default: return status;
    }
  };

  const backendBaseUrl = (() => {
    const sanitizeBaseUrl = (value) => {
      const raw = String(value || '').trim().replace(/\/+$/, '');
      if (!raw) return '';
      try {
        const parsed = new URL(raw);
        if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return parsed.origin;
      } catch (_) {}
      return '';
    };
    try {
      return sanitizeBaseUrl(localStorage.getItem('apiBaseUrl'))
        || sanitizeBaseUrl(process.env.REACT_APP_API_URL)
        || sanitizeBaseUrl(window.location.origin);
    } catch {
      return sanitizeBaseUrl(process.env.REACT_APP_API_URL) || sanitizeBaseUrl(window.location.origin);
    }
  })();

  const currentProfile = String(currentUser?.profile || currentUser?.type || '');
  const isSupervisorProfile = ['admin', 'supervisor', 'manager'].includes(currentProfile);
  const canInteractWithSelected = Boolean(
    selectedConversation?.id &&
    (String(selectedConversation?.userId || '') === String(currentUser?.id || '') || isSupervisorProfile)
  );

  const formatSeconds = (seconds) => {
    const s = Number(seconds || 0);
    const mm = Math.floor(s / 60);
    const ss = Math.floor(s % 60);
    return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  };

  const VoiceNotePlayer = ({ messageId, src, fromMe }) => {
    const audioRef = useRef(null);
    const [duration, setDuration] = useState(0);
    const [current, setCurrent] = useState(0);
    const isActive = String(activeAudioMessageId || '') === String(messageId || '');
    const isPlaying = isActive && audioRef.current && !audioRef.current.paused;

    useEffect(() => {
      const el = audioRef.current;
      if (!el) return;
      const onLoaded = () => setDuration(Number.isFinite(el.duration) ? el.duration : 0);
      const onTime = () => setCurrent(el.currentTime || 0);
      const onEnded = () => {
        setCurrent(0);
        setActiveAudioMessageId((cur) => (String(cur || '') === String(messageId || '') ? null : cur));
      };
      const onPause = () => {
        setCurrent(el.currentTime || 0);
      };
      el.addEventListener('loadedmetadata', onLoaded);
      el.addEventListener('timeupdate', onTime);
      el.addEventListener('ended', onEnded);
      el.addEventListener('pause', onPause);
      return () => {
        try { el.removeEventListener('loadedmetadata', onLoaded); } catch (_) {}
        try { el.removeEventListener('timeupdate', onTime); } catch (_) {}
        try { el.removeEventListener('ended', onEnded); } catch (_) {}
        try { el.removeEventListener('pause', onPause); } catch (_) {}
      };
    }, [messageId]);

    const toggle = async () => {
      const el = audioRef.current;
      if (!el) return;
      try {
        if (!el.paused) {
          el.pause();
          return;
        }
        if (activeAudioRef.current && activeAudioRef.current !== el) {
          try { activeAudioRef.current.pause(); } catch (_) {}
        }
        activeAudioRef.current = el;
        setActiveAudioMessageId(messageId);
        await el.play();
      } catch (_) {}
    };

    const seek = (_, value) => {
      const el = audioRef.current;
      if (!el) return;
      const v = Array.isArray(value) ? Number(value[0]) : Number(value);
      if (!Number.isFinite(v)) return;
      el.currentTime = Math.min(Math.max(v, 0), duration || 0);
      setCurrent(el.currentTime || 0);
    };

    return (
      <Box sx={{ minWidth: 220 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton size="small" onClick={toggle} sx={{ color: fromMe ? 'white' : 'inherit' }}>
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Slider
              size="small"
              value={Math.min(current, duration || 0)}
              min={0}
              max={Math.max(duration || 0, 1)}
              step={0.25}
              onChange={seek}
              sx={{
                color: fromMe ? 'rgba(255,255,255,0.9)' : 'primary.main',
                '& .MuiSlider-rail': { opacity: fromMe ? 0.35 : 0.25 }
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ opacity: 0.85, whiteSpace: 'nowrap' }}>
            {formatSeconds(current)} / {formatSeconds(duration)}
          </Typography>
        </Box>
        <audio ref={audioRef} src={src} preload="metadata" />
      </Box>
    );
  };

  const getMessageStatusIcon = (msg) => {
    if (!msg?.fromMe) return null;
    const status = msg.deliveryStatus || 'pending';
    if (status === 'failed') return <ErrorOutlineIcon fontSize="inherit" color="error" />;
    if (status === 'read') return <DoneAllIcon fontSize="inherit" color="primary" />;
    if (status === 'delivered') return <DoneAllIcon fontSize="inherit" />;
    if (status === 'sent') return <DoneIcon fontSize="inherit" />;
    return <ScheduleIcon fontSize="inherit" />;
  };

  const messageByProviderId = new Map(
    (messages || [])
      .filter(m => m?.messageId)
      .map(m => [m.messageId, m])
  );

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <StyledPaper elevation={0}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <QueueIcon sx={{ fontSize: 32 }} />
            <Typography variant="h4" fontWeight="bold">
              Workspace de Atendimento
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Atualizar filas">
              <IconButton onClick={fetchQueues} sx={{ color: 'white' }}>
                <QueueIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Atualizar conversas">
              <IconButton onClick={fetchConversations} sx={{ color: 'white' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </StyledPaper>

      <Grid container spacing={3}>
        {/* Sidebar - Lista de conversas */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, height: '80vh', overflow: 'hidden' }}>
            {/* Filtros */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <FilterIcon />
                Filtros
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Fila</InputLabel>
                <Select
                  value={selectedQueue}
                  onChange={(e) => setSelectedQueue(e.target.value)}
                  label="Fila"
                >
                  <MenuItem value="">Todas as Filas</MenuItem>
                  {queues.map((queue) => (
                    <MenuItem key={queue.id} value={queue.id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: queue.color || '#ccc'
                          }}
                        />
                        {queue.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="new">Novo</MenuItem>
                  <MenuItem value="triage">Triagem</MenuItem>
                  <MenuItem value="queued">Na fila</MenuItem>
                  <MenuItem value="assigned">Em atendimento</MenuItem>
                  <MenuItem value="pending_customer">Pendente cliente</MenuItem>
                  <MenuItem value="pending_internal">Pendente interno</MenuItem>
                  <MenuItem value="resolved">Resolvido</MenuItem>
                  <MenuItem value="closed">Fechado</MenuItem>
                  <MenuItem value="reopened">Reaberto</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Lista de conversas */}
            <Box sx={{ height: 'calc(100% - 200px)', overflowY: 'auto' }}>
              {loading ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <Typography>Carregando...</Typography>
                </Box>
              ) : conversations.length === 0 ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <Typography color="text.secondary">
                    Nenhuma conversa encontrada
                  </Typography>
                </Box>
              ) : (
                <List>
                  {conversations.map((conversation) => (
                    <ListItem
                      key={conversation.id}
                      button
                      onClick={() => handleConversationSelect(conversation)}
                      selected={selectedConversation?.id === conversation.id}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        '&.Mui-selected': {
                          backgroundColor: 'primary.light',
                          '&:hover': {
                            backgroundColor: 'primary.light',
                          },
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar src={conversation.contact?.profilePicUrl}>
                          <PersonIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div' }}
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2" component="span" noWrap>
                              {conversation.contact?.name || conversation.contact?.number}
                            </Typography>
                            <Chip
                              label={getStatusLabel(conversation.status)}
                              size="small"
                              color={getStatusColor(conversation.status)}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" component="span" color="text.secondary" noWrap>
                              {conversation.lastMessage || 'Sem mensagens'}
                            </Typography>
                            {conversation.queue && (
                              <Chip
                                label={conversation.queue.name}
                                size="small"
                                variant="outlined"
                                sx={{ 
                                  mt: 0.5,
                                  borderColor: conversation.queue.color,
                                  color: conversation.queue.color
                                }}
                              />
                            )}
                          </Box>
                        }
                      />
                      {!conversation.userId && (
                        <Tooltip title="Selecionar atendimento (confirmação)">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openConfirmAssign(conversation.id);
                            }}
                            sx={{ ml: 1 }}
                          >
                            <AcceptIcon color="success" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Chat Area */}
        <Grid item xs={12} md={6}>
          {selectedConversation ? (
            <Paper sx={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
              {/* Chat Header */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar src={selectedConversation.contact?.profilePicUrl}>
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {selectedConversation.contact?.name || selectedConversation.contact?.number}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedConversation.contact?.number}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" gap={1}>
                    <Tooltip title="Gerar protocolo">
                      <IconButton
                        onClick={() => handleGenerateProtocol(selectedConversation.id)}
                      >
                        <ReceiptIcon />
                      </IconButton>
                    </Tooltip>
                    {isSupervisorProfile && (
                      <Tooltip title="Supervisão">
                        <span>
                          <IconButton onClick={openSupervisionDialog} disabled={!selectedConversation?.id}>
                            <SupervisionIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Transferir ticket">
                      <span>
                        <IconButton onClick={openTransferDialog} disabled={!selectedConversation?.id}>
                          <TransferIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Alterar status">
                      <span>
                        <IconButton onClick={openStatusDialog} disabled={!selectedConversation?.id}>
                          <DoneIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {!selectedConversation?.userId || selectedConversation?.userId !== currentUser?.id ? (
                      <Tooltip title="Selecionar atendimento (confirmação)">
                        <IconButton onClick={() => openConfirmAssign(selectedConversation.id)}>
                          <AcceptIcon color="success" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Box display="flex" gap={1} alignItems="center">
                  <TextField
                    size="small"
                    placeholder="Buscar na conversa"
                    value={messageSearch}
                    onChange={(e) => setMessageSearch(e.target.value)}
                    fullWidth
                  />
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Tipo</InputLabel>
                    <Select value={messageTypeFilter} onChange={(e) => setMessageTypeFilter(e.target.value)} label="Tipo">
                      <MenuItem value="all">Todos</MenuItem>
                      <MenuItem value="text">Texto</MenuItem>
                      <MenuItem value="image">Imagem</MenuItem>
                      <MenuItem value="audio">Áudio</MenuItem>
                      <MenuItem value="video">Vídeo</MenuItem>
                      <MenuItem value="document">Documento</MenuItem>
                      <MenuItem value="sticker">Sticker</MenuItem>
                    </Select>
                  </FormControl>
                  <Tooltip title="Aplicar filtros">
                    <span>
                      <IconButton
                        onClick={() => fetchMessages(selectedConversation.id, { page: 1 })}
                        disabled={!selectedConversation?.id}
                      >
                        <SearchIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>

              {/* Messages Area */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
                {messages.length === 0 ? (
                  <Box 
                    display="flex" 
                    justifyContent="center" 
                    alignItems="center" 
                    height="100%" 
                    flexDirection="column"
                  >
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Nenhuma mensagem encontrada
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Selecione uma conversa para ver as mensagens
                    </Typography>
                  </Box>
                ) : (
                  renderedMessages.map((message, index) => (
                    <Box
                      key={
                        `msg-${message.messageId || message.id || message.timestamp || 'x'}-${index}`
                      }
                      onClick={() => setReplyTo(message)}
                      sx={{
                        display: 'flex',
                        justifyContent: message.fromMe ? 'flex-end' : 'flex-start',
                        mb: 1,
                      }}
                    >
                      <Paper
                        sx={{
                          p: 1,
                          maxWidth: '70%',
                          backgroundColor: message.fromMe
                            ? 'primary.main'
                            : (theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100]),
                          color: message.fromMe
                            ? 'white'
                            : (theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.text.primary),
                        }}
                      >
                        {message.quotedMsgId && messageByProviderId.get(message.quotedMsgId) && (
                          <Box sx={{ mb: 1, p: 1, borderLeft: '3px solid rgba(255,255,255,0.7)', backgroundColor: 'rgba(0,0,0,0.08)' }}>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                              Respondendo
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                              {messageByProviderId.get(message.quotedMsgId)?.body || '-'}
                            </Typography>
                          </Box>
                        )}

                        {(() => {
                          const mediaUrl = message.mediaUrl || message.fileUrl || message?.media?.url;
                          const mediaTypeRaw = message.mediaType || message.type || message?.media?.type || '';
                          const mediaType = String(mediaTypeRaw || '').toLowerCase();
                          if (!mediaUrl) return (<Typography variant="body2">{message.body}</Typography>);

                          const src = String(mediaUrl).startsWith('/')
                            ? `${backendBaseUrl}${mediaUrl}`
                            : String(mediaUrl);

                          const isAudio = mediaType === 'audio' || mediaType === 'ptt' || mediaType === 'voice' || mediaType.includes('audio') || String(message.body || '').includes('[Áudio]');
                          if (isAudio) return (<VoiceNotePlayer messageId={message.id} src={src} fromMe={message.fromMe} />);
                          if (mediaType === 'video') return (<video controls src={src} style={{ width: '100%', borderRadius: 8 }} />);
                          if (mediaType === 'image' || mediaType === 'sticker') return (<img src={src} alt="mídia" style={{ maxWidth: '100%', borderRadius: 8 }} />);
                          return (
                            <Button size="small" startIcon={<AttachFileIcon />} href={src} target="_blank" sx={{ color: message.fromMe ? 'white' : 'primary.main' }}>
                              Abrir arquivo
                            </Button>
                          );
                        })()}

                        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1} sx={{ mt: 0.5, opacity: 0.8 }}>
                          <Typography variant="caption">
                            {new Date(message.timestamp).toLocaleTimeString()}
                          </Typography>
                          {message.fromMe && (
                            <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                              {getMessageStatusIcon(message)}
                            </Typography>
                          )}
                        </Box>

                        {message.fromMe && message.deliveryStatus === 'failed' && (
                          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              startIcon={<ReplayIcon />}
                              sx={{ color: message.fromMe ? 'white' : 'primary.main' }}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!selectedConversation?.id) return;
                                try { await ticketService.resendMessage(selectedConversation.id, message.id); } catch {}
                              }}
                            >
                              Reenviar
                            </Button>
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  ))
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Message Input */}
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                {replyTo && (
                  <Paper sx={{ p: 1, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Respondendo</Typography>
                      <Typography variant="body2" noWrap>{replyTo.body}</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setReplyTo(null)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                )}
                {(isRecording || pendingMedia || pendingAudio) && (
                  <Paper sx={{ p: 1, mb: 1 }}>
                    {isRecording && (
                      <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="body2">Gravando áudio: {String(Math.floor(recordingSeconds / 60)).padStart(2, '0')}:{String(recordingSeconds % 60).padStart(2, '0')}</Typography>
                          <Button size="small" color="error" onClick={stopRecording}>Parar</Button>
                        </Box>
                        <LinearProgress />
                      </Box>
                    )}
                    {!isRecording && pendingAudio && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <audio controls src={pendingAudioPreviewUrl || undefined} style={{ width: '100%' }} />
                        <IconButton size="small" onClick={() => setPendingAudio(null)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                    {!isRecording && pendingMedia && (
                      <Box display="flex" alignItems="center" gap={1}>
                        {inferMediaType(pendingMedia) === 'image' || inferMediaType(pendingMedia) === 'sticker' ? (
                          <img src={pendingMediaPreviewUrl || undefined} alt="preview" style={{ maxHeight: 80, maxWidth: 120, borderRadius: 8 }} />
                        ) : inferMediaType(pendingMedia) === 'video' ? (
                          <video src={pendingMediaPreviewUrl || undefined} style={{ maxHeight: 80, maxWidth: 140, borderRadius: 8 }} />
                        ) : (
                          <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                            <AttachFileIcon fontSize="small" />
                            <Typography variant="body2" noWrap sx={{ flex: 1 }}>{pendingMedia?.name || 'Arquivo'}</Typography>
                          </Box>
                        )}
                        <IconButton size="small" onClick={() => setPendingMedia(null)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                )}
                <Box display="flex" gap={1}>
                  <input id="chat-file-input" type="file" style={{ display: 'none' }} onChange={handleFileSelect} />
                  <Tooltip title={pendingMedia ? `Anexo: ${pendingMedia.name}` : 'Anexar arquivo'}>
                    <span>
                      <IconButton onClick={() => document.getElementById('chat-file-input').click()} disabled={isSending || !selectedConversation || !canInteractWithSelected}>
                        <AttachFileIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={isRecording ? 'Parar gravação' : 'Gravar áudio pelo microfone'}>
                    <span>
                      <IconButton
                        onClick={() => (isRecording ? stopRecording() : startRecording())}
                        disabled={isSending || !selectedConversation || !canInteractWithSelected}
                        color={isRecording ? 'error' : 'default'}
                      >
                        <MicIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <TextField
                    fullWidth
                    placeholder="Digite sua mensagem..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (!isSending && e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    multiline
                    maxRows={4}
                    disabled={isSending || !selectedConversation || !canInteractWithSelected}
                    key={messageInputKey}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSendMessage}
                    disabled={isSending || (!newMessage.trim() && !pendingMedia && !pendingAudio) || !selectedConversation || !canInteractWithSelected}
                    sx={{ minWidth: 'auto', px: 2 }}
                  >
                    <SendIcon />
                  </Button>
                </Box>
              </Box>
            </Paper>
          ) : (
            <Paper sx={{ height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                Selecione uma conversa para começar
              </Typography>
            </Paper>
          )}
        </Grid>

        {/* Workspace Panel */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, height: '80vh', overflow: 'hidden' }}>
            {!selectedConversation ? (
              <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                <Typography color="text.secondary">Selecione um ticket</Typography>
              </Box>
            ) : loadingWorkspace ? (
              <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                <Typography color="text.secondary">Carregando...</Typography>
              </Box>
            ) : (
              <>
                <Tabs value={workspaceTab} onChange={(_, v) => setWorkspaceTab(v)} variant="fullWidth">
                  <Tab label="Ticket" />
                  <Tab label="Contato" />
                  <Tab label="Timeline" />
                  <Tab label="Interno" />
                </Tabs>
                <Divider sx={{ my: 1 }} />

                {workspaceTab === 0 && (
                  <Box sx={{ height: 'calc(100% - 56px)', overflowY: 'auto' }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2">Protocolo</Typography>
                      <Typography variant="body2">{workspace?.protocol || '-'}</Typography>
                      <Divider />
                      <Typography variant="subtitle2">Status</Typography>
                      <Chip label={getStatusLabel(workspace?.status)} size="small" color={getStatusColor(workspace?.status)} />
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>Prioridade</Typography>
                      <Typography variant="body2">{workspace?.priority || '-'}</Typography>
                      <Divider />
                      <Typography variant="subtitle2">Fila</Typography>
                      <Typography variant="body2">{workspace?.queue?.name || '-'}</Typography>
                      <Typography variant="subtitle2">Operador</Typography>
                      <Typography variant="body2">{workspace?.user?.name || '-'}</Typography>
                      <Divider />
                      <Typography variant="subtitle2">SLA</Typography>
                      <Typography variant="body2">
                        1ª resposta: {workspace?.sla?.firstResponseAt ? 'respondido' : (workspace?.sla?.firstResponseDueAt ? new Date(workspace.sla.firstResponseDueAt).toLocaleString() : '-')}
                      </Typography>
                      {workspace?.sla?.firstResponseStatus && !workspace?.sla?.firstResponseAt && (
                        <Chip
                          size="small"
                          label={workspace.sla.firstResponseStatus === 'within' ? 'Dentro do prazo' : workspace.sla.firstResponseStatus === 'near_due' ? 'Próximo do venc.' : 'Vencido'}
                          color={workspace.sla.firstResponseStatus === 'within' ? 'success' : workspace.sla.firstResponseStatus === 'near_due' ? 'warning' : 'error'}
                        />
                      )}
                      <Typography variant="body2">
                        Resolução: {workspace?.sla?.resolvedAt ? 'resolvido' : (workspace?.sla?.resolutionDueAt ? new Date(workspace.sla.resolutionDueAt).toLocaleString() : '-')}
                      </Typography>
                      {workspace?.sla?.resolutionStatus && !workspace?.sla?.resolvedAt && (
                        <Chip
                          size="small"
                          label={workspace.sla.resolutionStatus === 'within' ? 'Dentro do prazo' : workspace.sla.resolutionStatus === 'near_due' ? 'Próximo do venc.' : 'Vencido'}
                          color={workspace.sla.resolutionStatus === 'within' ? 'success' : workspace.sla.resolutionStatus === 'near_due' ? 'warning' : 'error'}
                        />
                      )}
                      {(workspace?.sla?.breachedFirstResponse || workspace?.sla?.breachedResolution) && (
                        <Chip label="SLA violado" color="error" size="small" />
                      )}
                      <Divider />
                      <Typography variant="subtitle2">Tags</Typography>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {(workspace?.tags || []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">Sem tags</Typography>
                        ) : (
                          workspace.tags.map(t => (
                            <Chip key={t.id} label={t.name} size="small" />
                          ))
                        )}
                      </Box>
                      <Divider />
                      <Typography variant="subtitle2">Contato</Typography>
                      <Typography variant="body2">{workspace?.contact?.name || '-'}</Typography>
                      <Typography variant="body2" color="text.secondary">{workspace?.contact?.number || '-'}</Typography>
                    </Stack>
                  </Box>
                )}

                {workspaceTab === 1 && (
                  <Box sx={{ height: 'calc(100% - 56px)', overflowY: 'auto' }}>
                    {!contactProfile ? (
                      <Typography variant="body2" color="text.secondary">Sem dados do contato</Typography>
                    ) : (
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">Nome</Typography>
                        <Typography variant="body2">{contactProfile.name}</Typography>
                        <Typography variant="subtitle2">Número</Typography>
                        <Typography variant="body2" color="text.secondary">{contactProfile.number}</Typography>
                        <Typography variant="subtitle2">Opt-in</Typography>
                        <Chip
                          size="small"
                          label={contactProfile.optInStatus || 'unknown'}
                          color={contactProfile.optInStatus === 'optin' ? 'success' : contactProfile.optInStatus === 'optout' ? 'error' : 'default'}
                        />
                        <Typography variant="subtitle2">Última interação</Typography>
                        <Typography variant="body2">
                          {contactProfile.lastInteractionAt ? new Date(contactProfile.lastInteractionAt).toLocaleString() : '-'}
                        </Typography>
                        <Divider />
                        <Typography variant="subtitle2">Observações</Typography>
                        <TextField
                          fullWidth
                          value={contactNotesDraft}
                          onChange={(e) => setContactNotesDraft(e.target.value)}
                          multiline
                          maxRows={4}
                        />
                        <Button
                          variant="outlined"
                          onClick={async () => {
                            try {
                              const updated = await contactsService.updateContact(contactProfile.id, { notes: contactNotesDraft });
                              const c = updated?.contact || updated?.data?.contact || updated?.data || null;
                              setContactProfile(c);
                              setContactNotesDraft(c?.notes || '');
                            } catch {}
                          }}
                        >
                          Salvar
                        </Button>
                        <Divider />
                        <Typography variant="subtitle2">Tags</Typography>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {(contactProfile.tags || []).length === 0 ? (
                            <Typography variant="body2" color="text.secondary">Sem tags</Typography>
                          ) : (
                            contactProfile.tags.map(t => (<Chip key={t.id} label={t.name} size="small" />))
                          )}
                        </Box>
                        <Divider />
                        <Typography variant="subtitle2">Últimos tickets</Typography>
                        {contactTickets.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">Sem tickets</Typography>
                        ) : (
                          <Stack spacing={1}>
                            {contactTickets.map(t => (
                              <Box key={t.id} sx={{ p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">{t.protocol || `#${t.id}`}</Typography>
                                <Typography variant="body2">{getStatusLabel(t.status)}</Typography>
                              </Box>
                            ))}
                          </Stack>
                        )}
                        <Divider />
                        <Typography variant="subtitle2">Timeline</Typography>
                        {contactTimeline.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">Sem eventos</Typography>
                        ) : (
                          <Stack spacing={1}>
                            {contactTimeline.slice(0, 20).map((item) => (
                              <Box key={`${item.kind}-${item.id}`} sx={{ p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">{item.at ? new Date(item.at).toLocaleString() : '-'}</Typography>
                                {item.kind === 'message' ? (
                                  <Typography variant="body2">{item.fromMe ? 'Você: ' : 'Cliente: '}{item.body}</Typography>
                                ) : (
                                  <Typography variant="body2">Ticket {item.protocol || item.id}: {getStatusLabel(item.status)}</Typography>
                                )}
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </Stack>
                    )}
                  </Box>
                )}

                {workspaceTab === 2 && (
                  <Box sx={{ height: 'calc(100% - 56px)', overflowY: 'auto' }}>
                    {timeline.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">Sem eventos</Typography>
                    ) : (
                      <Stack spacing={1}>
                        {timeline.map((item) => (
                          <Box key={`${item.kind}-${item.id}`} sx={{ p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {item.at ? new Date(item.at).toLocaleString() : '-'}
                            </Typography>
                            {item.kind === 'status' ? (
                              <Typography variant="body2">Status: {getStatusLabel(item.fromStatus)} → {getStatusLabel(item.toStatus)}</Typography>
                            ) : item.kind === 'note' ? (
                              <Typography variant="body2">Nota interna: {item.body}</Typography>
                            ) : (
                              <Typography variant="body2">{item.type}</Typography>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                )}

                {workspaceTab === 3 && (
                  <Box sx={{ height: 'calc(100% - 56px)', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                      {notes.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Sem notas internas</Typography>
                      ) : (
                        <Stack spacing={1}>
                          {notes.map((n) => (
                            <Box key={n.id} sx={{ p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {n.createdAt ? new Date(n.createdAt).toLocaleString() : '-'}
                              </Typography>
                              <Typography variant="body2">{n.body}</Typography>
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" gap={1}>
                      <TextField
                        fullWidth
                        placeholder="Adicionar nota interna..."
                        value={internalNote}
                        onChange={(e) => setInternalNote(e.target.value)}
                        multiline
                        maxRows={3}
                      />
                      <Button
                        variant="contained"
                        onClick={async () => {
                          const text = internalNote.trim();
                          if (!text || !selectedConversation?.id) return;
                          try {
                            await ticketService.createNote(selectedConversation.id, text);
                            setInternalNote('');
                            const n = await ticketService.getNotes(selectedConversation.id);
                            setNotes(n?.notes || n?.data?.notes || []);
                            const t = await ticketService.getTimeline(selectedConversation.id);
                            setTimeline(t?.timeline || t?.data?.timeline || []);
                          } catch {}
                        }}
                      >
                        Enviar
                      </Button>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Protocol Dialog */}
      <Dialog open={protocolDialog} onClose={() => setProtocolDialog(false)}>
        <DialogTitle>Protocolo Gerado</DialogTitle>
        <DialogContent>
          <Typography variant="h6" sx={{ textAlign: 'center', p: 2 }}>
            {generatedProtocol}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProtocolDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={transferDialogOpen} onClose={closeTransferDialog} fullWidth maxWidth="xs">
        <DialogTitle>Transferir ticket</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Operador</InputLabel>
            <Select value={transferToUserId} label="Operador" onChange={(e) => setTransferToUserId(e.target.value)}>
              {operators.map(o => {
                const id = o.id || o.userId;
                const label = o.name || `Operador ${id}`;
                return <MenuItem key={id} value={String(id)}>{label}</MenuItem>;
              })}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTransferDialog}>Cancelar</Button>
          <Button onClick={confirmTransfer} variant="contained" disabled={!transferToUserId}>Transferir</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={statusDialogOpen} onClose={closeStatusDialog} fullWidth maxWidth="xs">
        <DialogTitle>Alterar status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <RadioGroup value={statusDialogValue} onChange={(e) => setStatusDialogValue(e.target.value)}>
              <FormControlLabel value="closed" control={<Radio />} label="Fechar" />
              <FormControlLabel value="pending_customer" control={<Radio />} label="Pendente" />
            </RadioGroup>
          </FormControl>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Observação (obrigatória)"
            value={statusDialogNote}
            onChange={(e) => setStatusDialogNote(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStatusDialog}>Cancelar</Button>
          <Button onClick={confirmStatusChange} variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={supervisionDialogOpen} onClose={closeSupervisionDialog} fullWidth maxWidth="sm">
        <DialogTitle>Supervisão</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={2} mt={1} alignItems="center">
            <FormControl fullWidth>
              <InputLabel>Adicionar supervisor</InputLabel>
              <Select value={supervisionUserId} label="Adicionar supervisor" onChange={(e) => setSupervisionUserId(e.target.value)}>
                {supervisionCandidates.map(u => (
                  <MenuItem key={u.id} value={String(u.id)}>{u.name} ({String(u.type || u.profile)})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={addWatcher} disabled={!supervisionUserId}>Adicionar</Button>
          </Box>

          <Box mt={2}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Supervisores no ticket</Typography>
            {!watchers.length ? (
              <Typography variant="body2" color="text.secondary">Nenhum supervisor adicionado.</Typography>
            ) : (
              <List dense>
                {watchers.map(w => {
                  const uid = w.userId || w.user?.id;
                  const name = w.user?.name || `Usuário ${uid}`;
                  const type = String(w.user?.type || '');
                  return (
                    <ListItem
                      key={String(uid)}
                      secondaryAction={
                        <IconButton edge="end" onClick={() => removeWatcher(uid)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText primary={name} secondary={type} />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSupervisionDialog}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Assign Dialog */}
      <Dialog open={confirmAssignOpen} onClose={closeConfirmAssign}>
        <DialogTitle>Confirmar seleção</DialogTitle>
        <DialogContent>
          <Typography>Deseja assumir este atendimento? Você passará a ser o operador do ticket.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmAssign}>Cancelar</Button>
          <Button onClick={confirmAssign} variant="contained" color="primary">Confirmar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IntegratedWhatsAppChat;
